export type Coordinates = {
  lat: number
  lng: number
}

export type ParsedGoogleMapsInput =
  | { coordinates: Coordinates; kind: 'coordinates' }
  | { coordinates?: Coordinates; kind: 'maps-link'; label?: string }
  | { kind: 'short-link'; url: string }

const isValidCoordinates = ({ lat, lng }: Coordinates) =>
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180

const toCoordinates = (lat: string, lng: string): Coordinates | null => {
  const parsed = { lat: Number(lat), lng: Number(lng) }

  return Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng) && isValidCoordinates(parsed)
    ? parsed
    : null
}

const parseDecimalCoordinates = (value: string): Coordinates | null => {
  const match = value.trim().match(/^([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)$/)

  return match ? toCoordinates(match[1], match[2]) : null
}

const parseDmsCoordinate = (value: string, axis: 'lat' | 'lng'): null | number => {
  const match = value
    .trim()
    .match(/^(\d{1,3})°\s*(\d{1,2})['’′]\s*(\d{1,2}(?:\.\d+)?)"?\s*([NSEW])$/i)

  if (!match) return null

  const degrees = Number(match[1])
  const minutes = Number(match[2])
  const seconds = Number(match[3])
  const direction = match[4].toUpperCase()
  const validDirection =
    axis === 'lat' ? direction === 'N' || direction === 'S' : direction === 'E' || direction === 'W'

  if (
    !validDirection ||
    !Number.isFinite(degrees) ||
    !Number.isFinite(minutes) ||
    !Number.isFinite(seconds) ||
    minutes >= 60 ||
    seconds >= 60
  ) {
    return null
  }

  const coordinate = degrees + minutes / 60 + seconds / 3600

  return direction === 'S' || direction === 'W' ? -coordinate : coordinate
}

const parseDmsCoordinates = (value: string): Coordinates | null => {
  const match = value.trim().match(/^(.+?[NS])\s*[,\s]\s*(.+?[EW])$/i)

  if (!match) return null

  const lat = parseDmsCoordinate(match[1], 'lat')
  const lng = parseDmsCoordinate(match[2], 'lng')

  return lat === null || lng === null || !isValidCoordinates({ lat, lng }) ? null : { lat, lng }
}

const parseCoordinates = (value: string) =>
  parseDecimalCoordinates(value) ?? parseDmsCoordinates(value)

const isShortGoogleMapsUrl = (url: URL) =>
  (url.hostname === 'maps.app.goo.gl' && /^\/[A-Za-z0-9_-]+$/.test(url.pathname)) ||
  (url.hostname === 'goo.gl' && /^\/maps\/[A-Za-z0-9_-]+$/.test(url.pathname))

const getGoogleMapsLabel = (url: URL) => {
  const match = url.pathname.match(/^\/maps\/place\/([^/]+)/)

  if (!match) return undefined

  let label: string
  try {
    label = decodeURIComponent(match[1].replaceAll('+', ' ')).trim()
  } catch {
    return undefined
  }

  return parseCoordinates(label) ? undefined : label || undefined
}

const getDestinationCoordinates = (url: URL): Coordinates | null => {
  const dataCoordinates = [
    ...url.href.matchAll(/!3d([+-]?\d+(?:\.\d+)?)!4d([+-]?\d+(?:\.\d+)?)/g),
  ].at(-1)

  if (dataCoordinates) {
    return toCoordinates(dataCoordinates[1], dataCoordinates[2])
  }

  for (const key of ['q', 'query']) {
    const coordinates = parseCoordinates(url.searchParams.get(key) ?? '')
    if (coordinates) return coordinates
  }

  const pathCoordinates = url.pathname.match(
    /\/maps\/(?:place|search)\/([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)/,
  )

  return pathCoordinates ? toCoordinates(pathCoordinates[1], pathCoordinates[2]) : null
}

export const parseGoogleMapsInput = (value: string): null | ParsedGoogleMapsInput => {
  const coordinates = parseCoordinates(value)

  if (coordinates) return { coordinates, kind: 'coordinates' }

  let url: URL
  try {
    url = new URL(value.trim())
  } catch {
    return null
  }

  if (url.protocol !== 'https:' || url.username || url.password || url.port) return null
  if (isShortGoogleMapsUrl(url)) return { kind: 'short-link', url: url.toString() }
  if (
    !['google.com', 'www.google.com'].includes(url.hostname) ||
    !/^\/maps(?:\/|$)/.test(url.pathname)
  ) {
    return null
  }

  const destinationCoordinates = getDestinationCoordinates(url)
  const label = getGoogleMapsLabel(url)

  if (!destinationCoordinates && !label) return null

  return {
    ...(destinationCoordinates ? { coordinates: destinationCoordinates } : {}),
    kind: 'maps-link',
    label,
  }
}
